import React, { useContext } from 'react';
import { FormikContext, useFormikContext } from 'formik';

const sortByDOMPosition = (errorKeys: string[]): string[] => {
  const nullElements: any[] = [];

  const res = [...errorKeys];

  res.sort((a, b) => {
    let first = document.querySelector(`[name="${a}"]`);

    if (!first) {
      first = document.getElementById(a);
    }
    let second = document.querySelector(`[name="${b}"]`);

    if (!second) {
      second = document.getElementById(b);
    }

    if (!first) nullElements.push(a);
    if (!second) nullElements.push(b);

    if (!first || !second) return 0;

    // https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition
    const compared = first.compareDocumentPosition(second);

    if (compared === 4) {
      return -1;
    } else if (compared === 2) {
      return 1;
    }

    return 0;
  });

  const filteredRes = res.filter(key => {
    return !nullElements.includes(key);
  });

  return filteredRes;
};

// Gets lowest index error and returns the formik string related to the 'highest' positioned DOM node
const keyifyArray = (res: string[], el: string, prefix: string, obj: any) => {
  const indexedObj = obj[el].reduce((acc: any, item: any, index: any) => {
    if (!item) return acc;

    return {
      ...acc,
      [index]: {
        ...item,
      },
    };
  }, {});

  if (Object.keys(indexedObj).length > 0) {
    const smallestIndex = Object.keys(indexedObj).sort()[0];
    const namePrefix = `${prefix}${el}[${smallestIndex}]`;

    const keys = Object.keys(indexedObj[smallestIndex]).map(key => {
      return `${namePrefix}.${key}`;
    });

    const sortedKeys = sortByDOMPosition(keys);

    if (sortedKeys.length > 0) {
      const splitted = sortedKeys[0];

      return [...res, splitted];
    }
  }

  return res;
};

/*  Modified version of keyify from: https://gist.github.com/dphrag/4db3b453e02567a0bb52592679554a5b
    Returns all deeply-nested keys in an object in the form: n*<parentKey>.key, where parentKey is recursively appended to
*/
const keyify = (obj: { [key: string]: any }, prefix = ''): Array<string> => {
  return Object.keys(obj).reduce((res, el) => {
    if (Array.isArray(obj[el])) {
      return keyifyArray(res, el, prefix, obj);
    }

    if (typeof obj[el] === 'object' && obj[el] !== null && obj[el] !== undefined) {
      return [...res, ...keyify(obj[el], `${prefix}${el}.`)];
    }

    return [...res, prefix + el];
  }, [] as Array<string>);
};

// Keep for now, incase we need to filter error messages
// const removeDuplicates = (keyers: string[]) => {
//   const prefixes = keyers.map(key => {
//     const splitKey = key.split('.');
//     const end = splitKey.pop();
//     return [splitKey.join('.'), end];
//   });

//   const splitted = keyers.filter(key => {
//     const splitKey = key.split('.');
//     const end = splitKey.pop();
//     const prefix = splitKey.join('.');

//     const matches = prefixes.filter(matchPrefix => {
//       return matchPrefix[0] === prefix;
//     });

//     if (matches.length > 1 && end === 'ID') return false;
//     return true;
//   });

//   return splitted;
// };

// https://gist.github.com/dphrag/4db3b453e02567a0bb52592679554a5b
export const ScrollToError = () => {
  const { isSubmitting, isValidating, errors } = useFormikContext();

  React.useEffect(() => {
    const keyers = keyify(errors);

    if (keyers.length > 0 && isSubmitting && !isValidating) {
      const sortedErrors = sortByDOMPosition(keyers);

      let errorElement = null;

      errorElement = document.querySelector(`[name="${sortedErrors[0]}"]`) as HTMLElement;
      if (!errorElement) {
        errorElement = document.getElementById(`${sortedErrors[0]}`) as HTMLElement;
      }

      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });

        errorElement.focus({ preventScroll: true });
      }
    }
  }, [isSubmitting, isValidating, errors]);

  return null;
};

/** scroll to error when inside a Formik context, otherwise does nothing */
export const useScrollToError = () => {
  const formikContext = useContext(FormikContext);

  React.useEffect(() => {
    if (!formikContext) return;

    const keyers = keyify(formikContext.errors);

    if (keyers.length > 0 && formikContext.isSubmitting && !formikContext.isValidating) {
      const sortedErrors = sortByDOMPosition(keyers);

      let errorElement = null;

      errorElement = document.querySelector(`[name="${sortedErrors[0]}"]`) as HTMLElement;
      if (!errorElement) {
        errorElement = document.getElementById(`${sortedErrors[0]}`) as HTMLElement;
      }

      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });

        errorElement.focus({ preventScroll: true });
      }
    }
  }, [formikContext]);
};
